import { useEffect, useState } from 'react';
import { onboardingAPI } from '../../lib/api';
import type { FilledDataset } from '../../lib/api/types/onboarding';
import { auditsAPI, type AuditOutcome, type AuditStatus, type AuditType, type SupplierAuditDetail, type SupplierAuditPayload } from '../../lib/api/audits';
import { getErrorMessage } from '../../hooks/useFetchData';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AUDIT_TYPE_LABELS } from './auditStatus';
import { useAuditOptions } from './useAuditOptions';

interface AuditDialogProps {
  open: boolean;
  onClose: () => void;
  /** Present when editing; absent when planning a new audit. */
  audit?: SupplierAuditDetail | null;
  /** Pre-select the supplier when opened from a supplier context. */
  supplierId?: number;
  onSaved: (audit: SupplierAuditDetail) => void;
}

interface FormState {
  supplier: string;
  division: string;
  audit_type: AuditType;
  title: string;
  scheduled_date: string;
  completed_date: string;
  auditor: string;
  status: AuditStatus;
  outcome: AuditOutcome;
  score: string;
  summary: string;
  findings: string;
  checklist: string;
}

const NONE = '__none__';

function initialState(audit?: SupplierAuditDetail | null, supplierId?: number): FormState {
  return {
    supplier: audit ? String(audit.supplier) : supplierId ? String(supplierId) : '',
    division: audit?.division ? String(audit.division) : '',
    audit_type: audit?.audit_type ?? 'site',
    title: audit?.title ?? '',
    scheduled_date: audit?.scheduled_date ?? '',
    completed_date: audit?.completed_date ?? '',
    auditor: audit?.auditor ? String(audit.auditor) : '',
    status: audit?.status ?? 'planned',
    outcome: audit?.outcome ?? '',
    score: audit?.score ?? '',
    summary: audit?.summary ?? '',
    findings: audit?.findings ?? '',
    checklist: audit?.checklist ? String(audit.checklist) : '',
  };
}

/** Plan a new audit or change an existing one. Customer users only. */
export default function AuditDialog({ open, onClose, audit, supplierId, onSaved }: AuditDialogProps) {
  const [form, setForm] = useState<FormState>(() => initialState(audit, supplierId));
  const [saving, setSaving] = useState(false);
  const [checklists, setChecklists] = useState<FilledDataset[]>([]);
  const options = useAuditOptions(open);

  useEffect(() => {
    if (open) setForm(initialState(audit, supplierId));
  }, [open, audit, supplierId]);

  // The checklist is one of the questionnaires already assigned to that supplier.
  useEffect(() => {
    if (!open || !form.supplier) { setChecklists([]); return; }
    let cancelled = false;
    onboardingAPI.getFilledDatasets({ supplier: form.supplier, page_size: 100 })
      .then((res) => { if (!cancelled) setChecklists(res.data.results || []); })
      .catch((err: unknown) => { console.error('Checklist options failed:', err); if (!cancelled) setChecklists([]); });
    return () => { cancelled = true; };
  }, [open, form.supplier]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    if (!form.supplier) { toast.error('Pick the supplier being audited.'); return; }
    if (!form.title.trim()) { toast.error('Give the audit a title.'); return; }
    if (!form.scheduled_date) { toast.error('Pick the audit date.'); return; }
    const payload: SupplierAuditPayload = {
      supplier: Number(form.supplier),
      division: form.division ? Number(form.division) : null,
      audit_type: form.audit_type,
      title: form.title.trim(),
      scheduled_date: form.scheduled_date,
      completed_date: form.completed_date || null,
      auditor: form.auditor ? Number(form.auditor) : null,
      status: form.status,
      outcome: form.outcome,
      score: form.score === '' ? null : form.score,
      summary: form.summary,
      findings: form.findings,
      checklist: form.checklist ? Number(form.checklist) : null,
    };
    setSaving(true);
    try {
      const res = audit ? await auditsAPI.update(audit.id, payload) : await auditsAPI.create(payload);
      toast.success(audit ? 'Audit updated' : `Audit ${res.data.reference} planned`);
      onSaved(res.data);
      onClose();
    } catch (err: unknown) {
      console.error('Audit save failed:', err);
      toast.error(getErrorMessage(err, 'Could not save the audit'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="audit-dialog">
        <DialogHeader>
          <DialogTitle>{audit ? `Edit ${audit.reference}` : 'Plan an audit'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label>Supplier</Label>
            <SearchableSelect options={options.suppliers} value={form.supplier} onChange={(v) => set('supplier', v)}
                              placeholder={options.loading ? 'Loading suppliers…' : 'All suppliers — pick one'} />
          </div>
          <div className="space-y-1.5">
            <Label>Division</Label>
            <SearchableSelect options={options.divisions} value={form.division} onChange={(v) => set('division', v)}
                              placeholder="Group-wide" />
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={form.audit_type} onValueChange={(v) => set('audit_type', v as AuditType)}>
              <SelectTrigger data-testid="audit-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(AUDIT_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="audit-title">Title</Label>
            <Input id="audit-title" value={form.title} onChange={(e) => set('title', e.target.value)}
                   placeholder="e.g. Annual site audit — Leicester depot" data-testid="audit-title" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="audit-date">Scheduled date</Label>
            <Input id="audit-date" type="date" value={form.scheduled_date} onChange={(e) => set('scheduled_date', e.target.value)} data-testid="audit-date" />
          </div>
          <div className="space-y-1.5">
            <Label>Auditor</Label>
            <SearchableSelect options={options.people} value={form.auditor} onChange={(v) => set('auditor', v)} placeholder="Not yet assigned" />
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set('status', v as AuditStatus)}>
              <SelectTrigger data-testid="audit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Outcome</Label>
            <Select value={form.outcome || NONE} onValueChange={(v) => set('outcome', (v === NONE ? '' : v) as AuditOutcome)}>
              <SelectTrigger data-testid="audit-outcome-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not yet known</SelectItem>
                <SelectItem value="pass">Pass</SelectItem>
                <SelectItem value="conditional">Conditional pass</SelectItem>
                <SelectItem value="fail">Fail</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="audit-completed">Completed on</Label>
            <Input id="audit-completed" type="date" value={form.completed_date} onChange={(e) => set('completed_date', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="audit-score">Score (out of 100)</Label>
            <Input id="audit-score" type="number" min={0} max={100} step="0.1" value={form.score} onChange={(e) => set('score', e.target.value)} placeholder="—" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Checklist questionnaire</Label>
            <Select value={form.checklist || NONE} onValueChange={(v) => set('checklist', v === NONE ? '' : v)}>
              <SelectTrigger data-testid="audit-checklist-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {checklists.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.dataset_name || `Questionnaire #${c.dataset}`} · {c.status.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {form.supplier && checklists.length === 0
                ? 'No questionnaire is assigned to this supplier yet — assign one from the supplier\'s Questionnaires tab to use it as the checklist.'
                : 'A questionnaire already assigned to the supplier, used as this audit\'s checklist.'}
            </p>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="audit-summary">Scope and purpose</Label>
            <Textarea id="audit-summary" rows={3} value={form.summary} onChange={(e) => set('summary', e.target.value)} />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="audit-findings">Findings</Label>
            <Textarea id="audit-findings" rows={4} value={form.findings} onChange={(e) => set('findings', e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="audit-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {audit ? 'Save changes' : 'Plan audit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
